import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router'

import { Box, Flex, Grid, Text } from '@enechain/ecloud-designsystem'

import { PageBody } from '~/components/common/PageBody'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { iconSize } from '~/constants/iconSize'
import { visibleAdminHubCategories } from '~/pages/AdminHub/categories'
import { AdminHubSearch } from '~/pages/AdminHub/components/AdminHubSearch'
import { CompanyInfoCard } from '~/pages/AdminHub/components/CompanyInfoCard'

/** 中央寄せの最大幅 (design `css/layout.css` の `.content--center { max-width: 960px }`)。 */
const CENTER_MAX_WIDTH = '960px'

/**
 * システム管理トップ (`/admin/hub`)。
 *
 * 8080 プロト `admin/index.html` の hub (カテゴリ見出し + カード) に合わせた入口画面。
 * React mock `mock-app/react-app/src/pages/AdminHub` の dev 版。
 *
 * システム管理はサイドメニューが 23 項目と突出して多く (`SecondaryMenuContent` の
 * コメント参照)、メニューを開いて目で探す以外の入口が無かった。カテゴリごとに
 * カードで並べて、どの設定がどの束に属するかを一覧で読めるようにする。
 *
 * ## カードは icon・画面名・説明文を持つ (2026-10-05 デザイナー判断)
 *
 * 8080 の hub と同じく、 カードに 画面ごとの icon と、 その画面で何を扱うか
 * (中にどのタブがあるか) の説明文を出す。 サイドメニューの「システム管理」 は 1 リンクに
 * 畳んだ (#9228) ので、 ここが個別画面への唯一の入口になる。 画面名だけでは中身が読めず、
 * 「広域マスタ」 に BG・取引先があることなどを開いて確かめるしかなかった。
 * 説明文はハブ専用の文言 (`page:adminHub.cardDescription.*`) として持つ。 画面名は引き続き
 * `routePaths` の name を引くので、 メニュー名とカード名は食い違わない。
 * 初版は「文言の出所が増える」 ことを理由に説明文を持たなかったが、 上の理由でそれを改めた。
 *
 * 冒頭の画面説明 (PageIntro) は置かない (2026-10-05 デザイナー判断)。 カードの説明文が
 * 中身を語るので、 「カテゴリごとにまとめています」 は読んでも得るものが無かった。
 *
 * 横断検索は 2026-10-05 のデザイナー判断で 8080 と同じ形 (入力のたびに候補を出し、選ぶと移る)
 * を入れた ([[AdminHubSearch]])。 ただし対象は**画面名と区分名だけ**で、 8080 の別名・画面内見出し
 * は持たない (同じ理由で、 その元データが dev に無い)。 [spec 差異] 検索は spec (`admin/admin-hub.md`)
 * に未定義。
 *
 * ## 見た目は 8080 に揃える (2026-10-05 デザイナー判断)
 *
 * 区分を左右 2 列に並べ、 区分見出し (xl / 600 + 下線)、 カード (淡い accent 地の icon 箱 +
 * 見出し、 影付き) を design `admin/index.html` の `.hub-*` に合わせる。 カードの粒度だけは
 * spec「カード定義」 (画面 1 つ = カード 1 枚) に従う (8080 は束ねたカード)。 icon はカードごとに
 * 持つ (`categories.ts`)。 区分の並びは利用頻度の高い順 (同上)。 内容は 8080 の `.content--center` と同じく
 * 最大 960px で中央に寄せ、 左右に余白を取る。
 */
export const AdminHubPage = (): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('common:route.adminHub'),
          bold: true,
        },
      ]}
    >
      <PageBody>
        <PageScrollArea>
          {/* design `.content--center` (max-width 960px / 左右 auto) と同じく中央に寄せる。 */}
          <Flex
            flexDirection="column"
            gap={6}
            width="100%"
            maxWidth={CENTER_MAX_WIDTH}
            marginX="auto"
          >
            {/* 会社情報カードは画面上部 (admin-hub.md §2 機能詳細 3.)。 */}
            <CompanyInfoCard />
            {/* design `.hub-toolbar`: 会社情報の下、 区分の上に横断検索。 横並びの行に入れる
                (検索欄の `flex: 1 1 360px` は横方向の基準。 縦並びの親に直接置くと高さに効いて
                外枠が 360px の高さになり、 候補と × の位置がずれる)。 */}
            <Flex alignItems="center" flexWrap="wrap" gap={3}>
              <AdminHubSearch />
            </Flex>
            {/* design `.hub-content--system`: 区分を左右 2 列に並べ、 行ごとに横位置を揃える
                (column-count の段組みではなく grid)。 狭い幅では 1 列。 design は 900px 以下で
                1 列だが、 カード列が minmax(300px) のため 2 列だと 1 列あたり 300px を割る幅
                (サイドメニュー込みで lg 未満) では 1 列にする。
                表示できる画面が 1 つも無い区分は出さない (AC-AHB-001 / #6158)。 判定は
                `categories.ts` の visibleAdminHubCategories 側に置いている。 */}
            <Grid
              alignItems="start"
              gridRowGap={4}
              gridColumnGap={5}
              gridTemplateColumns={{ base: '1fr', lg: '1fr 1fr' }}
            >
              {visibleAdminHubCategories.map((category) => (
                <Flex key={category.titleKey} flexDirection="column" gap={3}>
                  {/* design `.hub-section-head` / `.hub-section-title` (xl / 600 + 下線)。 */}
                  <Box
                    paddingBottom={1}
                    borderBottomWidth="1px"
                    borderBottomColor="Border.lowEmphasis"
                  >
                    <Text
                      as="h2"
                      textStyle="hubSectionTitle"
                      color="Text.highEmphasis"
                    >
                      {t(category.titleKey)}
                    </Text>
                  </Box>
                  {/* design `.hub-grid`: repeat(auto-fill, minmax(300px, 1fr)) */}
                  <Grid
                    gridGap={3}
                    gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
                  >
                    {category.shortcuts.map(({ icon: Icon, ...shortcut }) => (
                      // design `.hub-card`: 淡い accent 地の icon 箱 + 見出し + 説明文、 影 sm (hover で md)。
                      // 説明文が 2 行に折れても icon は上端に揃える (design `align-items: flex-start`)。
                      <Flex
                        key={shortcut.path}
                        alignItems="flex-start"
                        gap={3}
                        padding={4}
                        textDecoration="none"
                        borderWidth="1px"
                        borderColor="Border.lowEmphasis"
                        borderRadius="lg"
                        boxShadow="shadSm"
                        _hover={{
                          boxShadow: 'shadMd',
                          borderColor: 'Border.midEmphasis',
                        }}
                        asChild
                        backgroundColor="Surface.primary"
                      >
                        <RouterLink to={shortcut.path}>
                          <Flex
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            width="40px"
                            height="40px"
                            color="Text.accentPrimary"
                            borderRadius="base"
                            backgroundColor="Surface.accentPrimaryLightLow"
                          >
                            <Icon
                              boxSize={iconSize.hubCardIcon}
                              aria-hidden="true"
                            />
                          </Flex>
                          {/* design `.hub-card-body`: 見出しと説明文を 3px 間隔で縦に積む */}
                          <Flex flexDirection="column" gap="3px" minWidth={0}>
                            <Text
                              textStyle="cardTitle"
                              color="Text.highEmphasis"
                            >
                              {t(shortcut.nameKey)}
                            </Text>
                            {/* design `.hub-card-desc`: sm / mid-emphasis / 行間 1.45 */}
                            <Text
                              textStyle="cardDescription"
                              color="Text.midEmphasis"
                            >
                              {t(shortcut.descriptionKey)}
                            </Text>
                          </Flex>
                        </RouterLink>
                      </Flex>
                    ))}
                  </Grid>
                </Flex>
              ))}
            </Grid>
          </Flex>
        </PageScrollArea>
      </PageBody>
    </PageLayout>
  )
}
